import { cloneMapDocument } from './serialize.ts';
import type { MapDocument } from './types.ts';

export type EditHistory = {
  past: MapDocument[];
  future: MapDocument[];
  limit: number;
};

export function createHistory(limit = 50): EditHistory {
  return { past: [], future: [], limit };
}

/** Snapshots the current map so it can be restored later, and discards the redo stack. */
export function pushUndo(history: EditHistory, snapshotBeforeChange: MapDocument): void {
  history.past.push(cloneMapDocument(snapshotBeforeChange));

  if (history.past.length > history.limit) {
    history.past.shift();
  }

  history.future = [];
}

export function canUndo(history: EditHistory): boolean {
  return history.past.length > 0;
}

export function canRedo(history: EditHistory): boolean {
  return history.future.length > 0;
}

/** Drops an in-progress undo snapshot without pushing it onto redo. */
export function abandonLast(history: EditHistory): void {
  history.past.pop();
}

/** Returns the previous map state, pushing `current` onto the redo stack, or null if none. */
export function undo(history: EditHistory, current: MapDocument): MapDocument | null {
  const previous = history.past.pop();

  if (!previous) {
    return null;
  }

  history.future.push(cloneMapDocument(current));
  return previous;
}

/** Returns the next map state, pushing `current` back onto the undo stack, or null if none. */
export function redo(history: EditHistory, current: MapDocument): MapDocument | null {
  const next = history.future.pop();

  if (!next) {
    return null;
  }

  history.past.push(cloneMapDocument(current));
  return next;
}
